/**
 * Analytics Reducer
 * NgRx reducer for analytics state management
 */
import { createReducer, on } from '@ngrx/store';
import { AnalyticsState, GroupBy } from '../models/analytics.models';
import * as AnalyticsActions from './analytics.actions';

export const initialState: AnalyticsState = {
  overview: null,
  documentAnalytics: null,
  apiUsageAnalytics: null,
  costAnalytics: null,
  templateAnalytics: null,
  providerAnalytics: null,
  dateRange: {
    start_date: new Date(new Date().setDate(new Date().getDate() - 7)),
    end_date: new Date()
  },
  groupBy: GroupBy.DAY,
  loading: false,
  error: null
};

export const analyticsReducer = createReducer(
  initialState,

  // Load Overview
  on(AnalyticsActions.loadOverview, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.loadOverviewSuccess, (state, { overview }) => ({
    ...state,
    overview,
    loading: false
  })),
  on(AnalyticsActions.loadOverviewFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Load Document Analytics
  on(AnalyticsActions.loadDocumentAnalytics, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.loadDocumentAnalyticsSuccess, (state, { data }) => ({
    ...state,
    documentAnalytics: data,
    loading: false
  })),
  on(AnalyticsActions.loadDocumentAnalyticsFailure, (state) => ({
    ...state,
    loading: false,
    documentAnalytics: null
  })),

  // Load API Usage Analytics
  on(AnalyticsActions.loadApiUsageAnalytics, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.loadApiUsageAnalyticsSuccess, (state, { data }) => ({
    ...state,
    apiUsageAnalytics: data,
    loading: false
  })),
  on(AnalyticsActions.loadApiUsageAnalyticsFailure, (state) => ({
    ...state,
    loading: false,
    apiUsageAnalytics: null
  })),

  // Load Cost Analytics
  on(AnalyticsActions.loadCostAnalytics, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.loadCostAnalyticsSuccess, (state, { data }) => ({
    ...state,
    costAnalytics: data,
    loading: false
  })),
  on(AnalyticsActions.loadCostAnalyticsFailure, (state) => ({
    ...state,
    loading: false,
    costAnalytics: null
  })),

  on(AnalyticsActions.loadTemplateAnalytics, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.loadTemplateAnalyticsSuccess, (state, { data }) => ({
    ...state,
    templateAnalytics: data,
    loading: false
  })),
  on(AnalyticsActions.loadTemplateAnalyticsFailure, (state) => ({
    ...state,
    loading: false,
    templateAnalytics: null
  })),

  on(AnalyticsActions.loadProviderAnalytics, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.loadProviderAnalyticsSuccess, (state, { data }) => ({
    ...state,
    providerAnalytics: data,
    loading: false
  })),
  on(AnalyticsActions.loadProviderAnalyticsFailure, (state) => ({
    ...state,
    loading: false,
    providerAnalytics: null
  })),

  // Export Report
  on(AnalyticsActions.exportReport, (state) => ({
    ...state,
    loading: true,
    error: null
  })),
  on(AnalyticsActions.exportReportSuccess, (state) => ({
    ...state,
    loading: false
  })),
  on(AnalyticsActions.exportReportFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),

  // Set Date Range
  on(AnalyticsActions.setDateRange, (state, { dateRange }) => ({
    ...state,
    dateRange
  })),

  // Set Group By
  on(AnalyticsActions.setGroupBy, (state, { groupBy }) => ({
    ...state,
    groupBy
  })),

  // Refresh All Analytics
  on(AnalyticsActions.refreshAllAnalytics, (state) => ({
    ...state,
    loading: true,
    error: null
  })),

  // Clear Analytics
  on(AnalyticsActions.clearAnalytics, () => initialState),

  // Clear Error
  on(AnalyticsActions.clearError, (state) => ({
    ...state,
    error: null
  }))
);

